import {
  AttributeSelectorType,
  CatalogResourceDc,
  EqlAttributesConfigurationDc,
  LayerReferenceConfigurationDc,
  OwnerFilter,
  QueryLayerServiceConfigurationDc,
  QueryLayerServiceInfoDc,
} from "@evergis/api";
import { isEmpty, intersection } from "lodash";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../../../../../evergis/api";
import { useNotification } from "../../../../../../hooks/notifications/useNotification";
import { useDefaultScaleRange } from "../../../../../../hooks/style/useDefaultScaleRange";
import { usePutTable } from "../../../../../../hooks/table/usePutTable";
import { useDialogReset } from "../../../../../../hooks/useDialogs";
import { useLoading } from "../../../../../../hooks/useLoading";
import { useTrigger } from "../../../../../../hooks/useTrigger";
import { filesCatalog } from "../../../../../../resources/catalog";
import { updateQueryLayer } from "../../../../../../resources/layers";
import { table } from "../../../../../../resources/tables";
import isValidLayerReference from "../../../../DialogComponents/Attributes/LayerReferences/isValidLayerReference";

export type UpdateQueryLayerOptions = {
  layer: QueryLayerServiceInfoDc;
  updateMeta: CatalogResourceDc;
  attributesConfiguration: EqlAttributesConfigurationDc;
  layerReferences?: LayerReferenceConfigurationDc[];
  eqlTables?: string[];
};

// eslint-disable-next-line max-lines-per-function
export const useUpdateQueryLayer = ({
  layer,
  updateMeta,
  attributesConfiguration,
  layerReferences,
  eqlTables = [],
}: UpdateQueryLayerOptions) => {
  const { t } = useTranslation("dialogs");
  const { name: alias, description, parentId, tags } = updateMeta;
  const [updateMutation, { data, loading, error }] = updateQueryLayer.use();
  const updating = useLoading(loading, !!data || !!error);

  const defaultScaleRange = useDefaultScaleRange();

  const { data: tableInfo } = table.use({
    input: eqlTables.length === 1 ? eqlTables[0] : void 0,
    waitInput: true,
  });

  const [updateTable] = usePutTable();

  // eslint-disable-next-line max-lines-per-function
  const update = useCallback(async () => {
    if (layer?.parentId !== parentId) {
      await api.catalog.moveResource(layer.resourceId, { targetResource: parentId });
      filesCatalog.invalidate({ ownerFilter: OwnerFilter.My, parentId });
      filesCatalog.invalidate({ ownerFilter: OwnerFilter.My, parentId: layer?.parentId });
    }

    const { attributes } = attributesConfiguration;

    const validLayerReferences = layerReferences ? layerReferences.filter(isValidLayerReference) : [];
    const layerReferencesIds = validLayerReferences.map(({ referenceId }) => referenceId);

    const allAttributes = [
      ...attributes.map(attribute => {
        if (!attribute.layerReferenceId) {
          return attribute;
        }

        if (isEmpty(intersection(layerReferencesIds, attribute.layerReferenceId))) {
          return { ...attribute, layerReferenceId: void 0, attributeSelectorType: AttributeSelectorType.None };
        }

        const filteredIds = attribute.layerReferenceId.filter(id => layerReferencesIds.includes(id));

        return {
          ...attribute,
          layerReferenceId: filteredIds,
          attributeSelectorType: isEmpty(filteredIds) ? AttributeSelectorType.None : attribute.attributeSelectorType,
        };
      }),
    ];

    return updateMutation({
      input: {
        configuration: {
          ...(layer.configuration as QueryLayerServiceConfigurationDc),
          attributesConfiguration: {
            ...attributesConfiguration,
            attributes: allAttributes,
            layerReferences: validLayerReferences,
          },
          alias,
          description,
          parentId,
          tags,
        },
        defaultScaleRange,
      },
    });
  }, [
    updateMutation,
    layer,
    alias,
    description,
    tags,
    parentId,
    defaultScaleRange,
    attributesConfiguration,
    layerReferences,
    tableInfo,
    updateTable,
  ]);

  const closeDialog = useDialogReset();

  useTrigger(data && !updating, closeDialog);

  useNotification(data, {
    success: true,
    title: t("editResource.changesApplied"),
    description: alias,
  });

  useNotification(error, {
    error: true,
    title: t("editResource.errorUpdatingLayer"),
    description: `${alias}. ${t("editResource.tryAgainOrCheckData")}`,
  });

  return useMemo(() => ({ update, data, updating }), [update, data, updating]);
};
